import Hero from 'features/home/hero/Hero'
import { Card, Grid, Heading, List, TextAndMedia, News, Testimonials, Cards, BREAKPOINTS, Icon } from '@boltenergy-be/design-system'
import { useTranslation } from 'next-i18next/pages'
import { Language } from 'types/language'
import { useRouter } from 'next/router'
import Link from 'components/link/Link'
import { routes } from 'utils/routes'
import { HomeProps } from 'features/home/types'
import {
  CURRENT_HERO_VARIANT,
  TESTIMONIALS_FR,
  TESTIMONIALS_NL,
  TRUSTPILOT_REVIEWS_FR,
  TRUSTPILOT_REVIEWS_NL
} from 'features/home/Home.constants'
import PlentySection from 'sections/plenty-section/PlentySection'
import ProducerCard from 'components/producer-card/ProducerCard'
import styles from './Home.module.scss'
import parse from 'html-react-parser'
import NextLink from 'next/link'
import { I18nUSP } from 'types/i18n'
import classNames from 'classnames'
import { useWindowSize } from 'usehooks-ts'
import { getUspLinkFromKey } from 'features/home/utils'
import ReviewRatings from 'components/review-ratings/ReviewRatings'
import { useFeatureFlagVariantKey } from 'posthog-js/react'
import { FEATURE_FLAGS } from 'constants/tracking'
import { useEffect, useState } from 'react'
import dayjs from 'dayjs'

const Home = ({ articles = [], categories = {}, producers = [] }: HomeProps) => {
  // Router
  const { locale } = useRouter()

  // i18n
  const { t } = useTranslation(['home', 'common'])

  // Window size - Necessary for extra spacing when showing social proof in StartSimulationCard.tsx for mobile/tablet only.
  const { width } = useWindowSize({ initializeWithValue: false })
  const isTablet = !!width && width < BREAKPOINTS.tablet // Based on $widthDesktop

  // Posthog
  // Feature flags resolve client-side only (and can already be cached in localStorage before hydration),
  const [hasMounted, setHasMounted] = useState(false)
  useEffect(() => setHasMounted(true), [])
  const variant = useFeatureFlagVariantKey(FEATURE_FLAGS.googleReviews)
  const showGoogleReviews = hasMounted && variant === 'google'

  // Constants
  const encyclopediaCategories = Object.entries(categories)

  return (
    <main>
      {/* HERO */}
      <Hero variant={CURRENT_HERO_VARIANT} />

      {/* TRUSTPILOT */}
      {showGoogleReviews && (
        <Grid
          mobileBehaviour="horizontal-scroll"
          data-variant={CURRENT_HERO_VARIANT}
          as="section"
          classnames={{
            content: styles.trustpilot,
            container: styles['trustpilot-container'],
            list: styles['review-list']
          }}
          title={t('trustpilot.title')}
          aboveTitleSlot={<ReviewRatings show={{ googleReviews: true, vnr: isTablet }} />}
        >
          {(locale === Language.FRENCH ? TRUSTPILOT_REVIEWS_FR : TRUSTPILOT_REVIEWS_NL).map((review) => (
            <Card key={review.documentId} color="white" as="figure" className={styles['trustpilot-card']}>
              <ul className={styles.rating} aria-label={`${review.rating} / 5`}>
                {Array.from({ length: 5 }, (_, index) => (
                  <li key={index}>
                    <Icon name="star" size={18} color={index < review.rating ? '#FFB800' : '#DADCE0'} />
                  </li>
                ))}
              </ul>

              {review.date && dayjs(review.date).isValid() && (
                <time dateTime={review.date}>{dayjs(review.date).format('D MMMM YYYY')}</time>
              )}

              <p className={styles.review}>{review.description}</p>

              <figcaption>
                <div data-text-color={review.thumbnailTextColor} className={styles.avatar} style={{ backgroundColor: review.thumbnailBg }}>
                  {review.author[0]}
                </div>
                <strong>{review.author}</strong>
              </figcaption>
            </Card>
          ))}
        </Grid>
      )}

      {/* WHAT */}
      <TextAndMedia
        data-variant={!showGoogleReviews ? CURRENT_HERO_VARIANT : undefined}
        classnames={{ container: classNames(styles['what-section'], { [styles['social-proof']]: isTablet }) }}
        media={{
          type: 'video',
          props: {
            publicId: `website/videos/bolt-producer-${locale === Language.FRENCH ? 'fr' : 'nl'}`,
            thumbnail: { publicId: 'website/videos/bolt-producer-thumbnail', fill: true, alt: '' }
          }
        }}
        title={parse(t('home:what.title'))}
        showMediaOnMobile
      >
        <p>{t('home:what.description')}</p>
      </TextAndMedia>

      {/* FOUR REASONS */}
      <Cards title={t('home:fourReasons.title')} color="light">
        {(t('common:usps', { returnObjects: true }) as I18nUSP[]).map(({ key, title, description }) => (
          <Card as="li" key={`usp-${key}`} color="soft">
            <Heading as="h2" variant="h4" serif>
              {title}
            </Heading>
            <p>{description}</p>
            <Link variant="inline" trailingIcon="chevronRight" className={styles.action} href={getUspLinkFromKey(key, locale)}>
              {t(`common:knowMore`)}
            </Link>
          </Card>
        ))}
      </Cards>

      {/* PRODUCERS */}
      {producers.length > 0 && (
        <Grid
          action={
            <Link representation="button" variant="secondary" href={routes(locale as Language).producers}>
              {t('home:producers.all')}
            </Link>
          }
          as="section"
          color="light"
          repeatCount="auto-fill"
          title={t('home:producers.title')}
        >
          {producers.map((producer) => (
            <ProducerCard key={producer.id} {...{ producer }} />
          ))}
        </Grid>
      )}

      {/* TESTIMONIALS */}
      <Testimonials
        color="accent-vibrant"
        entries={(locale === Language.DUTCH ? TESTIMONIALS_NL : TESTIMONIALS_FR).map((testimonial) => ({
          ...testimonial,
          quote: t(testimonial.quote)
        }))}
      />

      {/* PLENTY */}
      <PlentySection description={t('home:plenty.description')} title={t('home:plenty.title')} />

      {/* ENCYCLOPEDIA */}
      {locale === Language.DUTCH && encyclopediaCategories.length > 0 && (
        <Grid as="section" color="light" repeatCount="auto-fill" title={t('home:encyclopedia.title')}>
          {encyclopediaCategories.map(([category, { subCategories }]) => (
            <div key={category} className={styles['encyclopedia-category']}>
              <Heading as="h2" variant="h4" serif>
                {category}
              </Heading>

              <List as="ul" bullets="hidden">
                {subCategories.map((subCat) => (
                  <li key={subCat.slug}>
                    <Link href={subCat.slug} size="small" trailingIcon="chevronRight" variant="inline">
                      {subCat.title}
                    </Link>
                  </li>
                ))}
              </List>
            </div>
          ))}
        </Grid>
      )}

      {/* NEWS */}
      <News
        title={t('home:news.title')}
        articles={articles.map((article) => ({
          title: article.title,
          slug: article.slug,
          shortDescription: article.shortDescription,
          locale: article.locale as Language
        }))}
        formatHref={(slug, locale) => routes(locale, slug).blogArticle}
        // @ts-expect-error - href is a required prop in the Next link component. But it gets filled in the News component.
        linkElement={<NextLink />}
      />
    </main>
  )
}

export default Home
